import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  User,
  MapPin,
  Package,
  Euro,
  Globe,
  ArrowRight,
  X,
  FileUp,
  Sparkles,
} from 'lucide-react';
import api from '@/lib/api';

const DevisImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyZoneTariff, setApplyZoneTariff] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const pdfFile = acceptedFiles[0];
      if (pdfFile.type === 'application/pdf') {
        setFile(pdfFile);
        setPreview(null);
        setImportResult(null);
      } else {
        toast.error('Seuls les fichiers PDF sont acceptés');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
  });

  const handlePreview = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/devis-import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setPreview(response.data.preview);
      toast.success('PDF analysé avec succès');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse du PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview) return;

    setLoading(true);
    try {
      const response = await api.post('/devis-import/confirm', {
        extracted_data: preview.extracted_data,
        apply_zone_tariff: applyZoneTariff,
      });

      setImportResult(response.data);
      setConfirmDialogOpen(false);
      toast.success('Devis importé avec succès !');
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <div className="space-y-6" data-testid="devis-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Import de devis
          </h1>
          <p className="text-slate-500 mt-1">
            Importez des devis depuis des PDF (Odoo, etc.) avec extraction IA
          </p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 border-purple-200">
          <Sparkles className="w-3 h-3 mr-1" />
          Powered by AI
        </Badge>
      </div>

      {/* Result Card */}
      {importResult && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-800">Devis importé avec succès</h3>
                <p className="text-green-700 mt-1">
                  Référence : <span className="font-mono font-bold">{importResult.devis?.reference}</span>
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {importResult.client_created && (
                    <Badge className="bg-blue-100 text-blue-700">Nouveau client créé</Badge>
                  )}
                  <Badge className="bg-slate-100 text-slate-700">
                    Zone : {importResult.zone_detected}
                  </Badge>
                  <Badge className="bg-orange-100 text-orange-700">
                    Coefficient : x{importResult.coefficient_applied}
                  </Badge>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => navigate(`/admin/devis/${importResult.devis?.id}`)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Voir le devis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="outline" onClick={resetImport}>
                    Importer un autre PDF
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Zone */}
      {!importResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" />
              Étape 1 : Sélectionnez un PDF
            </CardTitle>
            <CardDescription>
              Glissez-déposez ou cliquez pour sélectionner un fichier PDF de devis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-orange-500 bg-orange-50'
                  : file
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-300 hover:border-orange-400 hover:bg-orange-50'
              }`}
            >
              <input {...getInputProps()} data-testid="file-input" />
              {file ? (
                <div className="flex flex-col items-center">
                  <FileText className="w-12 h-12 text-green-500 mb-3" />
                  <p className="font-medium text-green-700">{file.name}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {(file.size / 1024).toFixed(1)} Ko
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-slate-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetImport();
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Changer de fichier
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className={`w-12 h-12 mb-3 ${isDragActive ? 'text-orange-500' : 'text-slate-400'}`} />
                  <p className="font-medium text-slate-700">
                    {isDragActive ? 'Déposez le fichier ici' : 'Glissez-déposez un PDF'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">ou cliquez pour sélectionner</p>
                </div>
              )}
            </div>

            {file && !preview && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={handlePreview}
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="analyze-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Analyser avec l'IA
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Results */}
      {preview && !importResult && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Étape 2 : Vérifiez les données extraites
              </CardTitle>
              <CardDescription>
                L'IA a analysé le PDF. Vérifiez les informations avant de créer le devis.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Info */}
              <div>
                <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                  <User className="w-4 h-4" />
                  Client
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Nom</p>
                    <p className="font-medium">{preview.extracted_data?.client?.nom || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{preview.extracted_data?.client?.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Téléphone</p>
                    <p className="font-medium">{preview.extracted_data?.client?.telephone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Adresse</p>
                    <p className="font-medium">
                      {[
                        preview.extracted_data?.client?.adresse,
                        preview.extracted_data?.client?.code_postal,
                        preview.extracted_data?.client?.ville,
                        preview.extracted_data?.client?.pays,
                      ]
                        .filter(Boolean)
                        .join(', ') || '-'}
                    </p>
                  </div>
                </div>

                {/* Client Match Status */}
                <div className="mt-2">
                  {preview.client_match?.found ? (
                    <Badge className="bg-blue-100 text-blue-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Client existant trouvé : {preview.client_match.existing_client?.name}
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Nouveau client (sera créé)
                    </Badge>
                  )}
                </div>
              </div>

              <Separator />

              {/* Zone Detection */}
              <div>
                <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4" />
                  Zone tarifaire détectée
                </h4>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <Badge className="bg-purple-100 text-purple-700 text-sm px-3 py-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {preview.zone_detection?.zone_label}
                    </Badge>
                    <p className="text-sm text-slate-600 mt-2">
                      Coefficient appliqué : <span className="font-bold">x{preview.zone_detection?.coefficient}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="apply-tariff"
                      checked={applyZoneTariff}
                      onCheckedChange={setApplyZoneTariff}
                    />
                    <Label htmlFor="apply-tariff" className="text-sm">
                      Appliquer le coefficient
                    </Label>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Products/Lines */}
              <div>
                <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4" />
                  Lignes du devis
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Description</th>
                        <th className="px-4 py-2 text-right">Qté</th>
                        <th className="px-4 py-2 text-right">Prix HT</th>
                        <th className="px-4 py-2 text-right">Total HT</th>
                        <th className="px-4 py-2 text-center">Catalogue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {preview.products_preview?.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="font-medium">{item.ligne?.description}</p>
                            {item.ligne?.reference && (
                              <p className="text-xs text-slate-500">Réf: {item.ligne.reference}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">{item.ligne?.quantite}</td>
                          <td className="px-4 py-3 text-right font-mono">
                            {formatPrice(item.ligne?.prix_unitaire_ht)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-medium">
                            {formatPrice(item.ligne?.total_ht)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {item.product_found ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Trouvé
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-500 text-xs">
                                Non trouvé
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <Separator />

              {/* Totals */}
              <div>
                <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                  <Euro className="w-4 h-4" />
                  Totaux
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">Total HT</p>
                    <p className="text-xl font-bold font-mono">
                      {formatPrice(preview.extracted_data?.totaux?.total_ht)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">TVA</p>
                    <p className="text-xl font-bold font-mono">
                      {formatPrice(preview.extracted_data?.totaux?.total_tva)}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-sm text-orange-600">Total TTC</p>
                    <p className="text-xl font-bold font-mono text-orange-600">
                      {formatPrice(preview.extracted_data?.totaux?.total_ttc)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={resetImport}>
              Annuler
            </Button>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="import-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Créer le devis
            </Button>
          </div>
        </>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer l'import</DialogTitle>
            <DialogDescription>
              Un nouveau devis va être créé à partir des données extraites.
              {!preview?.client_match?.found && ' Un nouveau client sera également créé.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Client :</span>
              <span className="font-medium">{preview?.extracted_data?.client?.nom}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Zone tarifaire :</span>
              <Badge className="bg-purple-100 text-purple-700">
                {preview?.zone_detection?.zone_label}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Coefficient :</span>
              <span className="font-medium">
                {applyZoneTariff ? `x${preview?.zone_detection?.coefficient}` : 'Non appliqué'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Lignes :</span>
              <span className="font-medium">{preview?.products_preview?.length || 0}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleImport}
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DevisImportPage;
